import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { Search, Star, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

export default function AgencyCandidates() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [stages, setStages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/candidates", { params: { q: q || undefined, stage: stage || undefined } });
    setItems(data.items || []);
  };
  useEffect(() => { load(); api.get("/candidates/stages").then(r => setStages(r.data.stages)); /* eslint-disable-next-line */ }, [stage]);

  const openCand = async (id) => {
    const { data } = await api.get(`/candidates/${id}`);
    setSelected(data);
    setSummary("");
  };

  const updateRating = async (rating) => {
    await api.put(`/candidates/${selected.id}`, { rating });
    setSelected({ ...selected, rating });
    load();
  };

  const updateStage = async (s) => {
    await api.put(`/candidates/${selected.id}`, { pipeline_stage: s });
    setSelected({ ...selected, pipeline_stage: s });
    load();
    toast.success(`Moved to ${s}`);
  };

  const aiSummarize = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/summarize-resume", { candidate_id: selected.id });
      setSummary(data.text || data.raw || "No result");
    } catch { toast.error("AI unavailable"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="eyebrow mb-2">Agency Owner</div>
          <h1 className="font-display text-3xl font-bold text-navy">Candidate Database</h1>
        </div>
      </div>
      <div className="flex gap-3 mb-5 flex-wrap">
        <form onSubmit={(e) => {e.preventDefault(); load();}} className="flex-1 relative min-w-[240px]">
          <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search headline, skills, city…" className="pl-9 w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm" />
        </form>
        <select value={stage} onChange={e => setStage(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2.5 text-sm">
          <option value="">All stages</option>
          {stages.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div data-testid={TID.agencyCandidatesTable} className="border border-slate-200 rounded-xl bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr className="text-left text-slate-500">
              <th className="p-3">Name</th><th className="p-3">Headline</th><th className="p-3">City</th>
              <th className="p-3">Stage</th><th className="p-3">Rating</th><th className="p-3">Skills</th>
            </tr>
          </thead>
          <tbody>
            {items.map(c => (
              <tr key={c.id} onClick={() => openCand(c.id)} className="border-b last:border-0 cursor-pointer hover:bg-slate-50">
                <td className="p-3 font-medium text-navy">{c.user?.name || "—"}</td>
                <td className="p-3 text-slate-600">{c.headline}</td>
                <td className="p-3 text-slate-600">{c.city}{c.state ? `, ${c.state}` : ""}</td>
                <td className="p-3"><span className="px-2 py-1 rounded-full text-xs bg-slate-100">{c.pipeline_stage}</span></td>
                <td className="p-3">{"★".repeat(c.rating || 0)}<span className="text-slate-300">{"★".repeat(5 - (c.rating || 0))}</span></td>
                <td className="p-3 text-slate-500 text-xs">{(c.skills || []).slice(0, 4).join(", ")}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td className="p-6 text-center text-slate-500" colSpan={6}>No candidates yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setSelected(null)}>
          <div className="w-full md:w-[520px] bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <div className="font-display text-xl font-semibold text-navy">{selected.user?.name}</div>
                <div className="text-sm text-slate-500">{selected.headline}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5 text-sm">
              <div>
                <div className="eyebrow mb-1">Contact</div>
                <div>{selected.user?.email}</div>
                <div>{selected.phone}</div>
                <div>{selected.city}{selected.state ? `, ${selected.state}` : ""}</div>
              </div>
              <div>
                <div className="eyebrow mb-1">Rating</div>
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} onClick={() => updateRating(n)} className={`p-1 ${(selected.rating || 0) >= n ? "text-gold" : "text-slate-300"}`}>
                      <Star className="w-5 h-5 fill-current" />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="eyebrow mb-1">Pipeline Stage</div>
                <select value={selected.pipeline_stage} onChange={e => updateStage(e.target.value)} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                  {stages.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <div className="eyebrow mb-1">Skills</div>
                <div className="flex flex-wrap gap-1">
                  {(selected.skills || []).map(s => <span key={s} className="px-2 py-0.5 rounded-full bg-slate-100 text-xs">{s}</span>)}
                </div>
              </div>
              <div>
                <div className="eyebrow mb-1">Certifications</div>
                <div className="text-slate-600">{(selected.certifications || []).join(", ") || "—"}</div>
              </div>
              <div>
                <div className="eyebrow mb-1">Experience</div>
                <div className="space-y-2">
                  {(selected.experience || []).map((e, i) => (
                    <div key={i}>
                      <div className="font-medium text-navy">{e.title} — {e.company}</div>
                      <div className="text-xs text-slate-500">{e.start}–{e.end}</div>
                      <div className="text-slate-600">{e.description}</div>
                    </div>
                  ))}
                  {(!selected.experience || selected.experience.length === 0) && <div className="text-slate-500">—</div>}
                </div>
              </div>
              <div>
                <div className="eyebrow mb-1">Internal notes (private)</div>
                <textarea rows={3} defaultValue={selected.internal_notes || ""}
                  onBlur={async (e) => { await api.put(`/candidates/${selected.id}`, { internal_notes: e.target.value }); toast.success("Notes saved"); }}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <div className="eyebrow mb-2">AI</div>
                <button onClick={aiSummarize} disabled={busy} className="btn-navy py-2 px-4 text-sm">
                  <Sparkles className="w-4 h-4" /> {busy ? "Summarizing…" : "Summarize resume (AI)"}
                </button>
                {summary && (
                  <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-md text-slate-700 whitespace-pre-wrap">{summary}</div>
                )}
                <div className="text-[11px] text-slate-500 mt-2">AI provides suggestions only — you make the final decision. Ranking never considers protected characteristics.</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
